import { Role } from '@/modules/auth/types';
import { reactive } from 'vue';
import { RouteLocationRaw } from 'vue-router';

const rawMenus: MenuItemConfig[] = reactive([]);

export interface MenuItemConfig {
    parent?: string; // Dot-separated path to parent folder (e.g., 'General.Arena')
    key: string;
    name: string;
    route?: RouteLocationRaw;
    icon?: string;
    roles?: Role[];
}

export interface MenuNode {
    key: string;
    name: string;
    route?: RouteLocationRaw;
    icon?: string;
    roles?: Role[];
    children?: MenuNode[];
}

export const menuRegistry = {
    registerMenu(menu: MenuItemConfig | MenuItemConfig[]) {
        if (Array.isArray(menu)) {
            rawMenus.push(...menu);
        } else {
            rawMenus.push(menu);
        }
    },

    buildMenuTree(currentRoles: Role[]): MenuNode[] {
        const rootNodes: MenuNode[] = [];
        const nodeMap = new Map<string, MenuNode>();

        const getOrCreateNode = (fullPath: string, key: string, name: string): MenuNode => {
            const node = nodeMap.get(fullPath);
            if (node) {
                return node;
            }

            const parts = fullPath.split('.');
            if (parts.length > 1) {
                const parentPath = parts.slice(0, -1).join('.');
                const parentKey = parts[parts.length - 2];
                const parentNode = getOrCreateNode(parentPath, parentKey, parentKey);

                const newNode: MenuNode = { key, name, children: [], roles: [] };
                parentNode.children = parentNode.children || [];
                parentNode.children.push(newNode);
                nodeMap.set(fullPath, newNode);
                return newNode;
            } else {
                const newNode: MenuNode = { key, name, children: [], roles: [] };
                rootNodes.push(newNode);
                nodeMap.set(fullPath, newNode);
                return newNode;
            }
        };

        rawMenus.forEach(config => {
            const fullPath = config.parent ? `${config.parent}.${config.key}` : config.key;
            const node = getOrCreateNode(fullPath, config.key, config.name);

            node.name = config.name || node.name;
            node.route = config.route ?? node.route;
            node.icon = config.icon ?? node.icon;
            node.roles = config.roles ?? node.roles ?? [];
        });

        const sortNodes = (nodes: MenuNode[]) => {
            nodes.sort((a, b) => a.name.localeCompare(b.name));

            nodes.forEach(node => {
                if (node.children?.length) {
                    sortNodes(node.children);
                }
            });
        };

        sortNodes(rootNodes);

        const filterByRoles = (node: MenuNode): MenuNode | null => {
            const hasRole = !node.roles?.length || node.roles.some(role => currentRoles.includes(role));
            if (!hasRole) {
                return null;
            }

            return { ...node, children: (node.children ?? []).map(filterByRoles).filter(Boolean) };
        };

        return rootNodes.map(filterByRoles).filter(node => node != null);
    }
};
